import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ChefHat, Users, UtensilsCrossed, Package, Sparkles, Heart, Calculator } from 'lucide-react';
import { Button } from '../components/ui/button';
import { images, cateringServices } from '../data/mock';

const PitopalveluPage = () => {
  const serviceIcons = {
    "Juhlapitopalvelu": Users,
    "Kokoustarjoilu": UtensilsCrossed,
    "Astiavuokraus": Package
  };

  const serviceColors = {
    "Juhlapitopalvelu": "from-rose-500 to-pink-500",
    "Kokoustarjoilu": "from-emerald-500 to-emerald-500",
    "Astiavuokraus": "from-stone-600 to-stone-700"
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-rose-50 to-white">
      {/* Hero */}
      <section className="relative py-20 md:py-28 bg-gradient-to-br from-stone-800 via-stone-900 to-stone-800 overflow-hidden">
        <div className="absolute top-10 left-20 w-32 h-32 border-4 border-rose-500/20 rounded-full" />
        <div className="absolute bottom-10 right-10 w-48 h-48 border-4 border-rose-500/10 rounded-full" />
        
        <div className="absolute inset-0 opacity-20">
          <img
            src={images.saladBuffet}
            alt="Pitopalvelu"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 text-center">
          <span className="font-handwritten text-4xl text-rose-400 block mb-2">Juhlat tulossa?</span>
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-4">
            Pitopalvelu
          </h1>
          <p className="text-lg text-stone-300 max-w-2xl mx-auto">
            Järjestämme tarjoilun tilaisuuksiisi – 
            <span className="font-handwritten text-xl text-rose-400"> luotettavasti, joustavasti ja hyvällä maulla.</span>
          </p>
        </div>
      </section>

      {/* Intro - Playful */}
      <section className="py-16 md:py-24 bg-white relative overflow-hidden">
        <div className="absolute top-10 right-10 w-24 h-24 bg-rose-100 rounded-full opacity-50" />
        <div className="absolute bottom-10 left-10 w-32 h-32 bg-emerald-100 rounded-full opacity-40" />
        
        <div className="max-w-4xl mx-auto px-6 relative">
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-rose-500 to-pink-500 rounded-2xl mb-6 shadow-lg animate-wiggle">
              <ChefHat className="w-10 h-10 text-white" />
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-stone-800 mb-4">
              Ruoka juhliin, jotka <span className="font-handwritten text-rose-600">muistetaan</span>
            </h2>
            <p className="text-lg text-stone-600 leading-relaxed max-w-2xl mx-auto mb-8">
              Oli kyseessä syntymäpäivät, muistotilaisuus, häät tai työpaikan kokous – suunnittelemme tarjoilun yhdessä kanssasi niin, että se sopii juuri sinun tilaisuuteesi.
              <Heart className="w-5 h-5 text-rose-500 inline-block ml-2" />
            </p>
            
            {/* Buffetkone CTA */}
            <div className="bg-gradient-to-r from-emerald-50 to-amber-50 rounded-2xl p-6 border-2 border-dashed border-emerald-300 max-w-xl mx-auto">
              <p className="font-handwritten text-2xl text-emerald-600 mb-2">Laske hinta-arvio!</p>
              <p className="text-stone-600 text-sm mb-4">Rakenna oma pitopalvelukokonaisuutesi ja saat suuntaa-antavan hinnan samantien.</p>
              <Link to="/buffetkone">
                <Button className="bg-gradient-to-r from-emerald-500 to-emerald-500 hover:from-emerald-600 hover:to-emerald-600 text-white rounded-xl px-6 py-3 shadow-lg">
                  Laske hinta-arvio
                  <Calculator className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Services - Fun cards */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-stone-50 to-rose-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="font-handwritten text-3xl text-rose-500">Palvelumme</span>
            <h2 className="text-3xl md:text-4xl font-bold text-stone-800 mt-2">
              Mitä tarjoamme
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {cateringServices.map((service, index) => {
              const IconComponent = serviceIcons[service.title] || ChefHat;
              const gradientClass = serviceColors[service.title] || "from-rose-500 to-pink-500";
              return (
                <div
                  key={service.id}
                  className="bg-white rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all border-2 border-rose-100 card-hover"
                  style={{ transform: `rotate(${index % 2 === 0 ? '1' : '-1'}deg)` }}
                >
                  <div className={`w-16 h-16 bg-gradient-to-br ${gradientClass} rounded-2xl flex items-center justify-center mb-6 shadow-lg`}>
                    <IconComponent className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-stone-800 mb-3">{service.title}</h3>
                  <p className="text-stone-600 leading-relaxed">{service.description}</p>
                </div>
              );
            })}
          </div>
          
          {/* Astiavuokraus info */}
          <div className="mt-8 text-center">
            <p className="inline-block bg-rose-100 text-rose-700 px-6 py-3 rounded-full font-medium">
              Astiavuokraus kysyttäessä!
            </p>
          </div>
        </div>
      </section>

      {/* How it works - Steps */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <div className="absolute -inset-4 bg-rose-200 rounded-3xl rotate-2" />
              <img
                src={images.hotDishes}
                alt="Catering-tarjoilu"
                className="relative rounded-2xl shadow-xl w-full border-4 border-white"
              />
            </div>
            <div>
              <span className="font-handwritten text-3xl text-rose-500">Helppo homma!</span>
              <h2 className="text-3xl md:text-4xl font-bold text-stone-800 mb-8 mt-2">
                Näin tilaat pitopalvelun
              </h2>
              <div className="space-y-6">
                <div className="flex gap-4 bg-gradient-to-r from-emerald-50 to-amber-50 p-4 rounded-2xl border-2 border-dashed border-emerald-300 transform -rotate-[0.5deg] hover:rotate-0 transition-transform">
                  <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-emerald-500 to-emerald-500 text-white rounded-xl flex items-center justify-center shadow-lg">
                    <Calculator className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-stone-800">Kokeile pitopalvelulaskuriamme!</h3>
                    <p className="text-stone-600">Rakenna oma menusi ja saat suuntaa-antavan hinnan.</p>
                    <Link to="/buffetkone" className="text-emerald-600 hover:text-emerald-700 font-medium text-sm inline-flex items-center gap-1 mt-1">
                      Avaa laskuri <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                </div>
                <div className="flex gap-4 bg-rose-50 p-4 rounded-2xl border border-rose-100 transform rotate-[0.3deg] hover:rotate-0 transition-transform">
                  <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-rose-500 to-pink-500 text-white rounded-xl flex items-center justify-center font-bold text-xl shadow-lg">
                    1
                  </div>
                  <div>
                    <h3 className="font-semibold text-stone-800">Ota yhteyttä</h3>
                    <p className="text-stone-600">Kerro tilaisuutesi ajankohta, paikka ja henkilömäärä.</p>
                  </div>
                </div>
                <div className="flex gap-4 bg-stone-50 p-4 rounded-2xl border border-stone-100 transform -rotate-[0.3deg] hover:rotate-0 transition-transform">
                  <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-stone-600 to-stone-700 text-white rounded-xl flex items-center justify-center font-bold text-xl shadow-lg">
                    2
                  </div>
                  <div>
                    <h3 className="font-semibold text-stone-800">Suunnitellaan yhdessä</h3>
                    <p className="text-stone-600">Käymme läpi toiveesi ja koostamme sopivan menun.</p>
                  </div>
                </div>
                <div className="flex gap-4 bg-rose-50 p-4 rounded-2xl border border-rose-100 transform rotate-[0.2deg] hover:rotate-0 transition-transform">
                  <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-rose-500 to-pink-500 text-white rounded-xl flex items-center justify-center font-bold text-xl shadow-lg">
                    3
                  </div>
                  <div>
                    <h3 className="font-semibold text-stone-800">Me hoidamme loput</h3>
                    <p className="text-stone-600">Toimitamme ruoan ja tarvittaessa astiat. <span className="font-handwritten text-lg text-rose-600">Helppoa!</span></p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section - Fun */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-rose-500 to-pink-600 relative overflow-hidden">
        <div className="absolute top-10 left-10 w-32 h-32 border-4 border-white/10 rounded-full" />
        <div className="absolute bottom-10 right-10 w-48 h-48 border-4 border-white/5 rounded-full" />
        
        <div className="max-w-4xl mx-auto px-6 text-center relative">
          <span className="font-handwritten text-4xl text-rose-200 block mb-4">Kiinnostuitko?</span>
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">
            Tee juhlistasi onnistuneet – me hoidamme tarjoilun
          </h2>
          <p className="text-lg text-rose-100 mb-10 max-w-2xl mx-auto">
            Suunnittele kokonaisuus ja saat heti arvion hinnasta.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/buffetkone">
              <Button className="bg-white text-rose-600 hover:bg-rose-50 px-8 py-4 text-lg rounded-2xl shadow-xl hover:shadow-2xl transition-all group">
                Laske hinta-arvio
                <Calculator className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Link to="/yhteystiedot">
              <Button variant="outline" className="border-2 border-white/40 text-white hover:bg-white/10 px-8 py-4 text-lg rounded-2xl transition-all">
                tai ota suoraan yhteyttä
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PitopalveluPage;
